<template>
    <div class="page points-page">
        <div class="page-header">
            <h1>會員積分</h1>
            <p>查看和管理所有會員的積分資料</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄：篩選與操作同一套 grid 排版 -->
            <div class="toolbar toolbar-grid points-filter-toolbar">
                <div class="filters-grid">
                    <a-row :gutter="[16, 16]" align="bottom">
                        <a-col :xs="24" :md="14" :lg="16">
                            <div class="filter-field">
                                <span class="filter-field-label">搜尋會員</span>
                                <a-select
                                    show-search
                                    :filter-option="false"
                                    :options="userOptions"
                                    :loading="userLoading"
                                    :value="selectedUser"
                                    allow-clear
                                    placeholder="搜尋會員"
                                    size="middle"
                                    class="status-select w-full"
                                    @search="onUserSearch"
                                    @change="onUserSelect"
                                    @clear="onUserClear"
                                />
                            </div>
                        </a-col>
                        <a-col
                            :xs="24"
                            :md="10"
                            :lg="8"
                            class="points-toolbar-action-col"
                        >
                            <a-button
                                v-if="canAdjustPoints"
                                type="primary"
                                size="middle"
                                class="unified-add-btn points-adjust-btn"
                                @click="() => openAdjustModal()"
                            >
                                調整積分
                            </a-button>
                        </a-col>
                    </a-row>
                    <a-row
                        :gutter="[16, 16]"
                        class="points-filter-row-secondary"
                    >
                        <a-col :xs="24" :sm="8" :md="6" :lg="5">
                            <div class="filter-field">
                                <span class="filter-field-label">積分類型</span>
                                <a-select
                                    v-model:value="pointFilterType"
                                    :options="pointTypeFilterOptions"
                                    placeholder="全部"
                                    allow-clear
                                    size="middle"
                                    class="status-select w-full"
                                />
                            </div>
                        </a-col>
                        <a-col :xs="24" :sm="16" :md="10" :lg="7">
                            <div class="filter-field">
                                <span class="filter-field-label">數值區間</span>
                                <div class="amount-range-wrap">
                                    <a-input-number
                                        v-model:value="pointFilterMin"
                                        placeholder="最小數值"
                                        size="middle"
                                        :min="0"
                                        :precision="0"
                                        class="status-select amount-input w-full"
                                    />
                                    <span class="amount-sep">~</span>
                                    <a-input-number
                                        v-model:value="pointFilterMax"
                                        placeholder="最大數值"
                                        size="middle"
                                        :min="0"
                                        :precision="0"
                                        class="status-select amount-input w-full"
                                    />
                                </div>
                            </div>
                        </a-col>
                        <a-col :xs="24" :sm="24" :md="8" :lg="12">
                            <div class="filter-field filter-field--actions">
                                <span
                                    class="filter-field-label filter-field-label--placeholder"
                                >
                                    &nbsp;
                                </span>
                                <div class="toolbar-query-group">
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="handleRangeFilterSearch"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="handleRangeFilterReset"
                                    >
                                        重置
                                    </a-button>
                                </div>
                            </div>
                        </a-col>
                    </a-row>
                </div>
            </div>

            <!-- 積分列表表格 -->
            <DataTable
                title=""
                :columns="columns"
                :data-source="pointsList"
                :pagination="pagination"
                :loading="loading"
                :show-index="false"
                @refresh="loadPoints"
                @change="handleTableChange"
                class="points-table"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'user'">
                        <div class="user-info">
                            <a
                                v-if="record.userId"
                                class="user-link"
                                @click.stop="handleViewMember(record.userId)"
                            >
                                {{ formatUserName(record) }}
                            </a>
                            <span v-else class="user-name">
                                {{ formatUserName(record) }}
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'userId'">
                        <span>{{ record.userId }}</span>
                    </template>
                    <template v-else-if="column.key === 'types'">
                        <a-space wrap>
                            <template v-for="t in record.types" :key="t">
                                <a-popover
                                    v-if="canViewPoints"
                                    placement="bottomLeft"
                                    :overlay-class-name="'vaporwave-submenu-popover'"
                                    :mouse-enter-delay="0.05"
                                >
                                    <template #content>
                                        <div class="type-popover">
                                            <div class="type-popover-title">
                                                {{ t }}
                                            </div>
                                            <div class="type-popover-row">
                                                <a-tag color="cyan">
                                                    累計獲得：{{
                                                        record[
                                                            `${t}_cumulative_earned`
                                                        ] ?? 0
                                                    }}
                                                </a-tag>
                                                <a-tag color="blue">
                                                    可用：{{
                                                        record[`${t}_usable`] ??
                                                        0
                                                    }}
                                                </a-tag>
                                                <a-tag color="purple">
                                                    凍結：{{
                                                        record[`${t}_frozen`] ??
                                                        0
                                                    }}
                                                </a-tag>
                                                <a-tag color="green">
                                                    總額：{{
                                                        record[`${t}_total`] ??
                                                        0
                                                    }}
                                                </a-tag>
                                            </div>
                                        </div>
                                    </template>
                                    <a-tag class="type-chip">{{ t }}</a-tag>
                                </a-popover>
                                <a-tag v-else class="type-chip">{{ t }}</a-tag>
                            </template>
                        </a-space>
                    </template>
                    <template v-else-if="column.pointType">
                        <template v-if="canViewPoints">
                            <a-popover
                                placement="bottomLeft"
                                :get-popup-container="getPopupContainer"
                                :destroy-tooltip-on-hide="false"
                                trigger="click"
                                :open="
                                    openedCell ===
                                    `${record.userId}:${column.pointType}`
                                "
                                @openChange="
                                    (open) =>
                                        onCellOpenChange(
                                            open,
                                            record,
                                            column.pointType
                                        )
                                "
                            >
                                <template #content>
                                    <div class="type-popover">
                                        <div class="type-popover-title">
                                            {{ column.pointType }}
                                        </div>
                                        <div class="type-popover-row">
                                            <a-tag color="cyan">
                                                累計獲得：{{
                                                    record[
                                                        `${column.pointType}_cumulative_earned`
                                                    ] ?? 0
                                                }}
                                            </a-tag>
                                            <a-tag color="blue">
                                                可用：{{
                                                    record[
                                                        `${column.pointType}_usable`
                                                    ] ?? 0
                                                }}
                                            </a-tag>
                                            <a-tag color="purple">
                                                凍結：{{
                                                    record[
                                                        `${column.pointType}_frozen`
                                                    ] ?? 0
                                                }}
                                            </a-tag>
                                            <a-tag color="green">
                                                總額：{{
                                                    record[
                                                        `${column.pointType}_total`
                                                    ] ?? 0
                                                }}
                                            </a-tag>
                                        </div>
                                    </div>
                                </template>
                                <span class="points-value current" @click.stop>
                                    {{
                                        record[`${column.pointType}_usable`] ??
                                        0
                                    }}
                                </span>
                            </a-popover>
                        </template>
                        <template v-else>
                            <span class="points-value current">
                                {{ record[`${column.pointType}_usable`] ?? 0 }}
                            </span>
                        </template>
                    </template>
                    <template v-else-if="column.key === 'actions'">
                        <a-space class="action-buttons">
                            <a-button
                                v-if="canViewPoints"
                                size="small"
                                class="action-btn-view"
                                @click="viewUserPoints(record)"
                            >
                                查看
                            </a-button>
                            <a-button
                                v-if="canAdjustPoints"
                                size="small"
                                class="action-btn-edit"
                                @click="openAdjustModal(record)"
                            >
                                調整積分
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 查看會員積分總覽 Modal -->
        <a-modal
            v-model:open="historyModalVisible"
            :title="overviewTitle"
            width="800px"
            :footer="null"
        >
            <div class="history-content">
                <a-table
                    :columns="overviewColumns"
                    :data-source="userPointsOverview"
                    :loading="overviewLoading"
                    :pagination="false"
                    bordered
                    size="small"
                    class="modal-table"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'type'">
                            <a-tag color="purple">{{ record.type }}</a-tag>
                        </template>
                    </template>
                </a-table>
            </div>
        </a-modal>

        <!-- 調整積分 Modal -->
        <a-modal
            v-model:open="adjustModalVisible"
            :title="adjustTitle"
            width="820px"
            :confirm-loading="adjustLoading"
            :ok-button-props="{
                disabled:
                    !currentUser || !adjustForm.pointType || !adjustForm.amount
            }"
            ok-text="確定"
            cancel-text="取消"
            @ok="submitAdjust"
            @cancel="closeAdjustModal"
        >
            <a-form v-if="!adjustOpenedFromRow" layout="vertical" class="mb-3">
                <a-form-item label="選擇會員">
                    <a-select
                        show-search
                        :filter-option="false"
                        :options="userOptions"
                        :loading="userLoading"
                        :value="modalSelectedUser"
                        allow-clear
                        placeholder="搜尋並選擇會員"
                        size="middle"
                        class="status-select min-w-[300px]"
                        @search="onUserSearch"
                        @change="onModalUserSelect"
                        @clear="onModalUserClear"
                    />
                </a-form-item>
            </a-form>
            <div class="history-content mb-4">
                <a-table
                    :columns="overviewColumns"
                    :data-source="userPointsOverview"
                    :loading="overviewLoading"
                    :pagination="false"
                    bordered
                    size="small"
                    class="modal-table"
                />
            </div>

            <div :class="{ 'disabled-area': !currentUser }">
                <a-form layout="vertical">
                    <a-row :gutter="12">
                        <a-col :span="8">
                            <a-form-item label="積分類型">
                                <a-select
                                    v-model:value="adjustForm.pointType"
                                    :options="pointTypeSelectOptions"
                                    placeholder="選擇類型"
                                    :disabled="!currentUser"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="8">
                            <a-form-item label="增減">
                                <a-segmented
                                    v-model:value="adjustForm.direction"
                                    :options="['增加', '減少']"
                                    class="adjust-segmented"
                                    :disabled="!currentUser"
                                />
                            </a-form-item>
                        </a-col>
                        <a-col :span="8">
                            <a-form-item label="變動數值">
                                <a-input-number
                                    v-model:value="adjustForm.amount"
                                    :min="1"
                                    :step="1"
                                    class="w-full"
                                    :disabled="!currentUser"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>
                    <a-form-item label="調整原因">
                        <a-input
                            v-model:value="adjustForm.reason"
                            placeholder="請輸入原因"
                            allowClear
                            :disabled="!currentUser"
                        />
                    </a-form-item>
                </a-form>
                <a-divider />
                <div>
                    <div class="block-title">預覽</div>
                    <a-space>
                        <a-tag color="geekblue">
                            類型：{{ adjustForm.pointType || '-' }}
                        </a-tag>
                        <a-tag color="green">
                            可使用積分：{{ previewRow.usable }}
                        </a-tag>
                        <a-tag color="purple">
                            凍結積分：{{ previewRow.frozen }}
                        </a-tag>
                        <a-tag color="blue">
                            總積分：{{ previewRow.total }}
                        </a-tag>
                    </a-space>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, onBeforeUnmount } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useRouter } from 'vue-router';
import DataTable from '@/components/common/DataTable.vue';
import {
    getUserPoints,
    getUserPointsStatistics,
    adjustUserPoints,
    getPointTypeOptions
} from '@/api/points';
import { searchUsers } from '@/api/users';
import { useAdminInfo } from '@/stores/useAdminInfo';

const adminStore = useAdminInfo();
const router = useRouter();

// 格式化用戶名稱顯示（含 mail）
const formatUserName = (record) => {
    const name = record.userName || `#${record.userId}`;
    const mail = record.userMail || '';
    return mail ? `${name}(${mail})` : name;
};

// 查看會員詳情
const handleViewMember = (userId) => {
    if (!userId) return;
    if (!adminStore.hasPermissionCode('100110')) {
        // 會員詳情
        message.warning('您沒有查看會員詳情的權限');
        return;
    }
    router.push(`/members/${userId}`);
};

// （已移除）prod 權限快顯：window.__dumpPerms
const loading = ref(false);
const searchText = ref('');
const pointsList = ref([]);
const openedCell = ref('');
const PERM_VIEW_POINTS = '110102';
const PERM_ADJUST_POINTS = ['110103'];
// 檢視按鈕：擁有查看或可調整任一代碼即可顯示
const canViewPoints = computed(
    () =>
        adminStore.hasPermissionCode(PERM_VIEW_POINTS) ||
        adminStore.hasAnyPermissionCode(PERM_ADJUST_POINTS)
);
const canAdjustPoints = computed(() =>
    adminStore.hasAnyPermissionCode(PERM_ADJUST_POINTS)
);

// popover 控制容器
const getPopupContainer = () => document.body;

const onCellOpenChange = (open, record, type) => {
    openedCell.value = open ? `${record.userId}:${type}` : '';
};

// （已移除）watch 權限列印

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 筆`
});

// 積分類型清單（用於表格動態欄位）
const pointTypes = ref([]);
// 調整視窗用的積分類型選項（呼叫 /api/admin/point-types/options）
const pointTypeOptions = ref([]);
// 固定的積分類型顯示順序，未知類型排最後（字母序）
const POINT_TYPE_ORDER = ['CP', 'PP', 'EP', 'XP', 'GP', 'DIAMOND'];

// 表格欄位（每個積分類型各一欄，主顯示「可用」，hover 顯示詳細）
const columns = ref([
    {
        title: '#',
        key: 'userId',
        dataIndex: 'userId',
        width: 80,
        align: 'center'
    },
    {
        title: '會員名稱',
        key: 'user',
        width: 220
    },
    {
        title: '操作',
        key: 'actions',
        align: 'center',
        width: 160,
        fixed: 'right'
    }
]);

// 產生每個類型一欄（顯示可用，hover 顯示詳細）
const buildTypeUsableColumns = (types = []) => {
    return types.map((t) => ({
        title: t,
        key: `col_${t}`,
        pointType: t, // 自定義標記供 bodyCell 判斷
        align: 'center',
        width: 120,
        ellipsis: true,
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        sortOrder:
            sortState.pointTypeName === t
                ? sortState.sortOrder === 'asc'
                    ? 'ascend'
                    : sortState.sortOrder === 'desc'
                      ? 'descend'
                      : null
                : null
    }));
};

// 積分歷史記錄相關
const historyModalVisible = ref(false);
const currentUser = ref(null);

// 已移除未使用的歷史記錄相關狀態

// 積分數值區間篩選（積分類型 + 最小/最大）
const pointFilterType = ref('');
const pointFilterMin = ref(undefined);
const pointFilterMax = ref(undefined);
const pointTypeFilterOptions = computed(() => [
    { label: '全部', value: '' },
    ...pointTypeOptions.value
]);

// 會員遠端搜尋（輸入關鍵字 -> 叫用 API -> 顯示名稱，值為 id）
const selectedUser = ref(null);
const userLoading = ref(false);
const userOptions = ref([]);
let userSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250; // 會員搜尋 debounce 時間（毫秒）

const fetchUserOptions = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
        userOptions.value = [];
        return;
    }
    try {
        userLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];
            userOptions.value = arr.map((u) => {
                const name = u.name || u.user_name || `#${u.id}`;
                const mail = u.mail || u.email || '';
                const label = mail ? `${name}(${mail})` : name;
                return {
                    label,
                    value: u.id
                };
            });
        }
    } catch (e) {
        // 靜默失敗即可
    } finally {
        userLoading.value = false;
    }
};

const onUserSearch = (val) => {
    clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(
        () => fetchUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const onUserSelect = (val) => {
    selectedUser.value = val || null;
    pagination.current = 1;
    loadPoints();
};

const onUserClear = () => {
    selectedUser.value = null;
    loadPoints();
};

// Modal 內選會員（避免必須在頁面主區先選）
const modalSelectedUser = ref(null);
const onModalUserSelect = async (val) => {
    modalSelectedUser.value = val || null;
    if (modalSelectedUser.value) {
        currentUser.value = { userId: modalSelectedUser.value, userName: '' };
        await loadUserPointsOverview(currentUser.value.userId);
    } else {
        currentUser.value = null;
        userPointsOverview.value = [];
    }
};
const onModalUserClear = () => onModalUserSelect(null);

// 載入積分列表
const loadPoints = async () => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            search: searchText.value
        };

        if (sortState.sortBy && sortState.sortOrder) {
            params.sort_by = sortState.sortBy;
            params.sort_order = sortState.sortOrder;
            if (sortState.pointTypeName)
                params.point_type_name = sortState.pointTypeName;
        }

        if (selectedUser.value) {
            params.user_id = selectedUser.value;
        }

        if (pointFilterType.value) {
            params.point_type_name = pointFilterType.value;
            params.point_type = pointFilterType.value;
        }
        if (pointFilterMin.value != null && pointFilterMin.value !== '') {
            params.point_type_min = Number(pointFilterMin.value);
        }
        if (pointFilterMax.value != null && pointFilterMax.value !== '') {
            params.point_type_max = Number(pointFilterMax.value);
        }

        const response = await getUserPoints(params);

        if (response.result) {
            const list = response.data?.data || [];
            // 取得所有出現過的 point_type
            const types = Array.from(
                new Set(
                    list.flatMap((u) =>
                        (u.points || []).map((p) => p.point_type)
                    )
                )
            );
            // 依固定順序排序，不在清單的排最後並以字母序
            const sortedTypes = types.sort((a, b) => {
                const ia = POINT_TYPE_ORDER.indexOf(a);
                const ib = POINT_TYPE_ORDER.indexOf(b);
                if (ia === -1 && ib === -1) return a.localeCompare(b);
                if (ia === -1) return 1;
                if (ib === -1) return -1;
                return ia - ib;
            });
            pointTypes.value = sortedTypes;

            // 動態產生欄位（會員資訊 + 動態欄 + 操作）
            const idCol = {
                title: '#',
                key: 'userId',
                dataIndex: 'userId',
                width: 80,
                align: 'center'
            };
            const baseUserCol = { title: '會員名稱', key: 'user', width: 220 };
            const actionCol = {
                title: '操作',
                key: 'actions',
                align: 'center',
                width: 160,
                fixed: 'right'
            };
            columns.value = [
                idCol,
                baseUserCol,
                ...buildTypeUsableColumns(sortedTypes),
                actionCol
            ];

            // 將資料扁平化到每筆 row
            pointsList.value = list.map((u) => {
                const row = {
                    userId: u.user_id,
                    userName: u.user_name,
                    userMail: u.mail || u.email || '',
                    types: sortedTypes
                };
                sortedTypes.forEach((t) => {
                    const found = (u.points || []).find(
                        (p) => p.point_type === t
                    ) || { total: 0, usable: 0, frozen: 0 };
                    row[`${t}_usable`] = found.usable ?? 0;
                    row[`${t}_frozen`] = found.frozen ?? 0;
                    row[`${t}_total`] = found.total ?? 0;
                    row[`${t}_cumulative_earned`] =
                        found.cumulative_earned ?? 0;
                });
                return row;
            });

            const pag = response.data?.pagination || {};
            pagination.total = pag.total ?? response.data?.total ?? 0;
            pagination.current =
                pag.current_page ?? response.data?.current_page ?? 1;
            pagination.pageSize = pag.per_page ?? pagination.pageSize;
        } else {
            showErrorMessage(response.message || '載入失敗');
        }
    } catch (error) {
        console.error('載入積分列表失敗:', error);
        showErrorMessage('載入積分列表失敗');
    } finally {
        loading.value = false;
    }
};

// 無 segmented 模式，欄位僅依類型重建

// 處理表格變化
// 伺服器端排序：將欄位排序動作轉換為 API 參數
const sortState = reactive({ sortBy: '', sortOrder: '', pointTypeName: '' });
const handleTableChange = async ({ pagination: pag, sorter }) => {
    // DataTable 以單一物件事件傳入
    pagination.current = pag?.current || 1;
    pagination.pageSize = pag?.pageSize || pagination.pageSize;

    // antd 可能回傳陣列（多欄排序），我們只取第一個
    const s = Array.isArray(sorter) ? sorter[0] : sorter;

    if (s && s.order && s.column && s.column.pointType) {
        sortState.sortBy = 'point_type_name';
        sortState.pointTypeName = s.column.pointType;
        sortState.sortOrder = s.order === 'ascend' ? 'asc' : 'desc';
    } else if (!s || !s.order) {
        sortState.sortBy = '';
        sortState.pointTypeName = '';
        sortState.sortOrder = '';
    }
    await loadPoints();
};

// 搜尋
const handleSearch = () => {
    pagination.current = 1;
    loadPoints();
};

// 積分區間篩選：查詢
const handleRangeFilterSearch = () => {
    pagination.current = 1;
    loadPoints();
};

// 積分區間篩選：重置
const handleRangeFilterReset = () => {
    pointFilterType.value = '';
    pointFilterMin.value = undefined;
    pointFilterMax.value = undefined;
    pagination.current = 1;
    loadPoints();
};

// 刷新
const handleRefresh = () => {
    loadPoints();
};

// 查看會員積分總覽
const viewUserPoints = async (record) => {
    currentUser.value = record;
    historyModalVisible.value = true;
    await loadUserPointsOverview(record.userId);
};

// 讀取會員積分總覽
const overviewLoading = ref(false);
const userPointsOverview = ref([]);
const overviewColumns = [
    { title: '類型', key: 'type', dataIndex: 'type', width: 100 },
    {
        title: '累計獲得',
        key: 'cumulative_earned',
        dataIndex: 'cumulative_earned',
        align: 'center',
        width: 140
    },
    {
        title: '可使用積分',
        key: 'usable',
        dataIndex: 'usable',
        align: 'center',
        width: 140
    },
    {
        title: '凍結積分',
        key: 'frozen',
        dataIndex: 'frozen',
        align: 'center',
        width: 120
    },
    {
        title: '總積分',
        key: 'total',
        dataIndex: 'total',
        align: 'center',
        width: 120
    }
];
const overviewTitle = computed(() => {
    const u = currentUser.value;
    if (!u) return '會員積分';
    const display = u.userName
        ? `${u.userName} (#${u.userId})`
        : `#${u.userId}`;
    return `會員積分 - ${display}`;
});

const adjustTitle = computed(() => {
    const u = currentUser.value;
    if (!u) return '調整積分';
    const display = u.userName
        ? `${u.userName} (#${u.userId})`
        : `#${u.userId}`;
    return `調整積分 - ${display}`;
});

const loadUserPointsOverview = async (userId) => {
    try {
        overviewLoading.value = true;
        const res = await getUserPointsStatistics(userId);
        if (res.result) {
            const data = res.data || {};
            const rows = Object.keys(data).map((type) => ({
                type,
                cumulative_earned: data[type]?.cumulative_earned ?? 0,
                usable: data[type]?.usable ?? 0,
                frozen: data[type]?.frozen ?? 0,
                total: data[type]?.total ?? 0
            }));
            userPointsOverview.value = rows.sort((a, b) => {
                const ia = POINT_TYPE_ORDER.indexOf(a.type);
                const ib = POINT_TYPE_ORDER.indexOf(b.type);
                if (ia === -1 && ib === -1) return a.type.localeCompare(b.type);
                if (ia === -1) return 1;
                if (ib === -1) return -1;
                return ia - ib;
            });
        } else {
            showErrorMessage(res.message || '載入失敗');
        }
    } catch (e) {
        console.error('載入會員積分失敗:', e);
        showErrorMessage('載入會員積分失敗');
    } finally {
        overviewLoading.value = false;
    }
};

// 調整積分流程
const adjustModalVisible = ref(false);
const adjustLoading = ref(false);
const adjustForm = reactive({
    pointType: null,
    direction: '增加',
    amount: 1,
    reason: ''
});
const pointTypeSelectOptions = computed(() => pointTypeOptions.value);

// 載入積分類型選項（僅供調整視窗使用）
const loadPointTypeOptions = async () => {
    try {
        const res = await getPointTypeOptions();
        if (res.result) {
            const data = res.data || {};
            const codes = Object.keys(data);
            const sorted = codes.sort((a, b) => {
                const ia = POINT_TYPE_ORDER.indexOf(a);
                const ib = POINT_TYPE_ORDER.indexOf(b);
                if (ia === -1 && ib === -1) return a.localeCompare(b);
                if (ia === -1) return 1;
                if (ib === -1) return -1;
                return ia - ib;
            });
            pointTypeOptions.value = sorted.map((code) => ({
                // 僅顯示英文代碼
                label: code,
                value: code
            }));
        } else {
            showErrorMessage(res.message || '載入積分類型選項失敗');
        }
    } catch (e) {
        console.error('載入積分類型選項失敗:', e);
        showErrorMessage('載入積分類型選項失敗');
    }
};

const openAdjustModal = async (userRecord) => {
    // 先清空上一次狀態，避免沿用上一位會員
    currentUser.value = null;
    adjustOpenedFromRow.value = false;

    // 來自 row 的情況：直接帶入該會員，不顯示選擇器
    if (userRecord && userRecord.userId) {
        currentUser.value = {
            userId: userRecord.userId,
            userName: userRecord.userName
        };
        adjustOpenedFromRow.value = true;
    } else if (selectedUser.value) {
        // 若工具列已選過會員，沿用
        currentUser.value = { userId: selectedUser.value, userName: '' };
        adjustOpenedFromRow.value = false;
    }

    adjustModalVisible.value = true;

    // 先載入選單選項
    await loadPointTypeOptions();

    if (currentUser.value) {
        await loadUserPointsOverview(currentUser.value.userId);
        adjustForm.pointType = pointTypeOptions.value[0]?.value || null;
        adjustForm.direction = '增加';
        adjustForm.amount = 1;
        adjustForm.reason = '';
    } else {
        // 尚未選擇會員：清空上半部與表單，等待使用者在視窗內選擇
        userPointsOverview.value = [];
        adjustOpenedFromRow.value = false;
        adjustForm.pointType = pointTypeOptions.value[0]?.value || null;
        adjustForm.direction = '增加';
        adjustForm.amount = 1;
        adjustForm.reason = '';
    }
};

const closeAdjustModal = () => {
    adjustModalVisible.value = false;
    // 關閉時也清空狀態，避免下次開啟殘留
    currentUser.value = null;
    userPointsOverview.value = [];
    adjustOpenedFromRow.value = false;
    adjustForm.pointType = pointTypeOptions.value[0]?.value || null;
    adjustForm.direction = '增加';
    adjustForm.amount = 1;
    adjustForm.reason = '';
};

const submitAdjust = async () => {
    if (!currentUser.value) return;
    if (!adjustForm.pointType) {
        message.warning('請選擇積分類型');
        return;
    }
    if (!adjustForm.amount || adjustForm.amount <= 0) {
        message.warning('請輸入正整數變動數值');
        return;
    }
    try {
        adjustLoading.value = true;
        const payload = {
            point_type: adjustForm.pointType,
            change_amount:
                adjustForm.direction === '增加'
                    ? adjustForm.amount
                    : -adjustForm.amount,
            reason: adjustForm.reason || '手動調整'
        };
        const res = await adjustUserPoints(currentUser.value.userId, payload);
        if (res.result) {
            message.success('已調整積分');
            await loadUserPointsOverview(currentUser.value.userId);
            await loadPoints();
            adjustModalVisible.value = false;
        } else {
            showErrorMessage(res.message || '調整失敗');
        }
    } catch (e) {
        console.error('調整積分失敗:', e);
        showErrorMessage('調整積分失敗');
    } finally {
        adjustLoading.value = false;
    }
};

// 即時預覽（基於目前總覽 + 表單輸入試算）
const previewRow = computed(() => {
    const type = adjustForm.pointType;
    if (!type) return { usable: 0, frozen: 0, total: 0 };
    const current = userPointsOverview.value.find((r) => r.type === type) || {
        usable: 0,
        frozen: 0,
        total: 0
    };
    const delta =
        (adjustForm.direction === '增加' ? 1 : -1) *
        (Number(adjustForm.amount) || 0);
    const usable = Math.max(0, current.usable + delta);
    const total = Math.max(0, current.total + delta);
    return { usable, frozen: current.frozen, total };
});

// 來源狀態：是否由 row 觸發
const adjustOpenedFromRow = ref(false);

// 移除歷史記錄用的表格變更處理

// 已移除未使用的 adjustPoints 與日期格式化工具

onMounted(() => {
    loadPoints();
    // 頁面初始也預先載入一次選項
    loadPointTypeOptions();
});

onBeforeUnmount(() => {
    clearTimeout(userSearchTimer);
    userSearchTimer = null;
});
</script>

<style scoped>
.points-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    min-height: 400px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

.toolbar {
    position: relative;
    z-index: 1;
}

.points-filter-row-secondary {
    margin-top: 4px;
}

.points-toolbar-action-col {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
}

.points-adjust-btn {
    width: 100%;
}

@media (min-width: 768px) {
    .points-adjust-btn {
        width: auto;
        min-width: 120px;
    }
}

/* 下拉選單樣式 - 與會員管理/角色管理一致 */
.toolbar :deep(.status-select .ant-select) {
    border-radius: 8px !important;
}

.filters-grid :deep(.status-select .ant-select-selector),
.toolbar :deep(.status-select .ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
}

.filters-grid :deep(.status-select .ant-select-selector:hover),
.toolbar :deep(.status-select .ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

.filters-grid :deep(.status-select .ant-select-focused .ant-select-selector),
.toolbar :deep(.status-select .ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

.filters-grid :deep(.status-select .ant-select-selection-item),
.toolbar :deep(.status-select .ant-select-selection-item) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

.filters-grid :deep(.status-select .ant-select-selection-placeholder),
.toolbar :deep(.status-select .ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

.filters-grid :deep(.status-select .ant-select-arrow),
.toolbar :deep(.status-select .ant-select-arrow) {
    color: rgba(42, 42, 58, 0.6) !important;
    transition: color 0.3s ease !important;
}

.filters-grid :deep(.status-select .ant-select:hover .ant-select-arrow),
.toolbar :deep(.status-select .ant-select:hover .ant-select-arrow) {
    color: #2a4a7a !important;
}

/* 下拉選單下拉框樣式 */
.filters-grid :deep(.status-select .ant-select-dropdown),
.toolbar :deep(.status-select .ant-select-dropdown) {
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 12px !important;
    box-shadow: 0 0 25px rgba(42, 74, 122, 0.25) !important;
    padding: 8px !important;
    margin-top: 4px !important;
}

.filters-grid :deep(.status-select .ant-select-item),
.toolbar :deep(.status-select .ant-select-item) {
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    transition: all 0.2s ease !important;
}

.filters-grid :deep(.status-select .ant-select-item:hover),
.toolbar :deep(.status-select .ant-select-item:hover) {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a4a7a !important;
}

.filters-grid :deep(.status-select .ant-select-item-option-selected),
.toolbar :deep(.status-select .ant-select-item-option-selected) {
    background: rgba(42, 74, 122, 0.25) !important;
    color: #2a4a7a !important;
    font-weight: 600 !important;
}

.filters-grid :deep(.status-select .ant-select-item-option-active),
.toolbar :deep(.status-select .ant-select-item-option-active) {
    background: rgba(42, 74, 122, 0.15) !important;
}

/* Modal 中的下拉選單樣式 */
:deep(.ant-modal .status-select .ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.1) !important;
}

:deep(.ant-modal .status-select .ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.2) !important;
}

:deep(.ant-modal .status-select .ant-select-focused .ant-select-selector) {
    border-color: #2a4a7a !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
}

:deep(.ant-modal .status-select .ant-select-selection-item),
:deep(.ant-modal .status-select .ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

:deep(.ant-modal .status-select .ant-select-selection-placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
}

:deep(.ant-modal .status-select .ant-select-arrow) {
    color: rgba(42, 42, 58, 0.6) !important;
    transition: color 0.3s ease !important;
}

:deep(.ant-modal .status-select .ant-select:hover .ant-select-arrow) {
    color: #2a4a7a !important;
}

/* DataTable component integration - 與會員列表統一 */
:deep(.ant-card) {
    background: transparent !important;
    border: none !important;
}

:deep(.ant-card-body) {
    padding: 0 !important;
}

/* 用戶信息樣式 */
.user-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.user-name {
    color: #2a2a3a;
    font-weight: 500;
}

.user-link {
    color: #3b6ea5;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    font-family: 'Courier New', monospace;
}

.user-link:hover {
    color: #2a4a7a;
    text-decoration: underline;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.3);
}

.user-email {
    color: rgba(42, 42, 58, 0.8);
    font-size: 12px;
}

/* 積分數值樣式 */
.points-value {
    font-weight: 600;
    font-family: 'Courier New', monospace;
}

.points-value.current {
    color: #2a2a3a;
    /* 提升對比，避免過淺看不清楚 */
    font-size: 16px;
    font-weight: 700;
}

.points-hint {
    display: inline-block;
    margin-left: 6px;
    color: rgba(42, 42, 58, 0.5);
    font-size: 12px;
}

.points-value.earned {
    color: #52c41a;
}

.points-value.used {
    color: #ff4d4f;
}

/* 日期文字 */
.date-text {
    color: rgba(42, 42, 58, 0.8);
}

/* 歷史記錄 Modal */
.history-content {
    max-height: 500px;
    overflow-y: auto;
}

/* 區塊卡片感與表格邊框 */
.block-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    padding: 12px;
}

.block-title {
    font-weight: 700;
    color: #2a2a3a;
    margin-bottom: 8px;
}

.modal-table :deep(.ant-table) {
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 8px !important;
}

/* 未選會員時的 disabled 視覺 */
.disabled-area {
    opacity: 0.5;
    pointer-events: none;
}

/* Segmented 選中態強化：紫色底 + 亮邊框 + 粗體 */
:deep(.adjust-segmented .ant-segmented-item-selected) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    color: #0d0d0d !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(42, 74, 122, 0.4) !important;
}

:deep(.adjust-segmented .ant-segmented-item) {
    transition: all 0.2s ease;
}

:deep(.adjust-segmented .ant-segmented-item:hover) {
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.3);
}

.points-change {
    font-weight: 600;
    font-size: 16px;
}

.points-change.positive {
    color: #52c41a;
}

.points-change.negative {
    color: #ff4d4f;
}

/* 操作按鈕樣式 - 參考會員管理頁面 */
.points-table .action-buttons,
.action-buttons {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
}

.action-buttons :deep(.ant-space-item) {
    margin-right: 4px !important;
}
</style>
